import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Dropdown,
  Flex,
  Popconfirm,
  Switch,
  Tag,
  theme,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  BookOpen,
  History as HistoryIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Zap
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ProtocolEnum,
  organizationsOrganizationsCustomAuthProvidersCreateMutation,
  organizationsOrganizationsCustomAuthProvidersDestroyMutation,
  organizationsOrganizationsCustomAuthProvidersListOptions,
  organizationsOrganizationsCustomAuthProvidersListQueryKey,
  organizationsOrganizationsCustomAuthProvidersPartialUpdateMutation,
  type OrganizationCustomAuthProvider
} from 'src/api/main'
import {
  MonoValue,
  ProTableContainer,
  QueryErrorAlert,
  resolveIconColors,
  TableActionColumnTitle
} from 'src/components'
import { DOCS_LINKS } from 'src/constants'
import { useQueryParams } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { ProviderLogo } from '../WorkspaceCustomProvidersPanel/ProviderLogo'

import { CustomProviderDrawer } from './CustomProviderDrawer'
import { useStyles } from './styles'
import { selectCustomProviders } from './utils'

const { Text, Link } = Typography

type DrawerState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; provider: OrganizationCustomAuthProvider }

/**
 * Menu-item label for actions whose backend endpoint does not exist yet
 * (test connection, rotate secret, per-provider audit). The item stays
 * `disabled` — the badge tells the admin it's planned, not broken.
 */
const soonLabel = (text: string) => <>{text}</>

const protocolColor = (protocol: ProtocolEnum) => {
  if (protocol === ProtocolEnum.OIDC) {
    return '#0F766E'
  }
  if (protocol === ProtocolEnum.SAML2) {
    return '#D97706'
  }
  return '#2563EB'
}

type Props = {
  workspaceId: number
  idValid: boolean
}

export const CustomProvidersTab = ({ workspaceId, idValid }: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { styles } = useStyles()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [drawer, setDrawer] = useState<DrawerState>({ mode: 'closed' })

  const params = useQueryParams()
  const searchTerm = String(params.search ?? '').trim()

  const queryKey = organizationsOrganizationsCustomAuthProvidersListQueryKey({
    path: { id: workspaceId }
  })
  const {
    data: providers = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsOrganizationsCustomAuthProvidersListOptions({
      path: { id: workspaceId }
    }),
    enabled: idValid,
    select: selectCustomProviders
  })

  /**
   * Search and sort run in the browser, not on the API.
   *
   * The schema advertises `search` / `ordering` / `page` / `per_page` on this
   * path, but they are inherited from the parent ViewSet and the action itself
   * ignores them: `custom_auth_providers` in `apps/organizations/views.py`
   * serialises `custom_auth_providers.all().order_by("date_created")` and
   * returns a bare array. Sending those params would be a no-op. The full set
   * arrives in one response, so filtering it here is both correct and cheap.
   *
   * The term still lives in the URL (`?search=`), written by the container's
   * own SearchInput, so the view stays shareable and reload-safe.
   */
  const visibleProviders = useMemo(() => {
    if (!searchTerm) return providers

    const needle = searchTerm.toLowerCase()

    return providers.filter((provider) =>
      [provider.name, provider.slug, provider.issuer_url, provider.protocol]
        .filter((field): field is string => Boolean(field))
        .some((field) => field.toLowerCase().includes(needle))
    )
  }, [providers, searchTerm])

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey, exact: false })

  const createMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersCreateMutation(),
    onSuccess: () => {
      message.success(
        t('workspace.authentication.customProviders.messages.created')
      )
      void invalidate()
      setDrawer({ mode: 'closed' })
    },
    onError: (e) => showApiErrors(e)
  })

  const updateMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersPartialUpdateMutation(),
    onSuccess: () => {
      message.success(
        t('workspace.authentication.customProviders.messages.updated')
      )
      void invalidate()
      setDrawer({ mode: 'closed' })
    },
    onError: (e) => showApiErrors(e)
  })

  const deleteMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersDestroyMutation(),
    onSuccess: () => {
      message.success(
        t('workspace.authentication.customProviders.messages.deleted')
      )
      void invalidate()
    },
    onError: (e) => showApiErrors(e)
  })

  const toggleMutation = useMutation({
    ...organizationsOrganizationsCustomAuthProvidersPartialUpdateMutation(),
    onSuccess: () => {
      void invalidate()
    },
    onError: (e) => showApiErrors(e)
  })

  const columns: ColumnsType<OrganizationCustomAuthProvider> = useMemo(
    () => [
      {
        title: t('workspace.authentication.customProviders.columns.name'),
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_value: string, record) => {
          const tile = resolveIconColors(token, protocolColor(record.protocol))

          return (
            <Flex align="center" gap={9}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: tile.backgroundColor,
                  color: tile.iconColor,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {record.logo ? (
                  <ProviderLogo value={record.logo} size={16} />
                ) : (
                  <ShieldCheck size={14} />
                )}
              </span>
              <Flex
                vertical
                style={{ lineHeight: 1.25, minWidth: 0, maxWidth: 320 }}
              >
                <Link
                  strong
                  style={{ fontSize: 13 }}
                  onClick={() => setDrawer({ mode: 'edit', provider: record })}
                >
                  {record.name}
                </Link>
                <Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  ellipsis={{ tooltip: record.issuer_url ?? '' }}
                >
                  {record.issuer_url ?? '—'}
                </Text>
              </Flex>
            </Flex>
          )
        }
      },
      {
        title: t('workspace.authentication.customProviders.columns.slug'),
        dataIndex: 'slug',
        key: 'slug',
        width: 180,
        sorter: (a, b) => a.slug.localeCompare(b.slug),
        render: (slug: string) => <MonoValue ellipsis>{slug}</MonoValue>
      },
      {
        title: t('workspace.authentication.customProviders.columns.protocol'),
        dataIndex: 'protocol',
        key: 'protocol',
        width: 110,
        sorter: (a, b) => a.protocol.localeCompare(b.protocol),
        render: (protocol: ProtocolEnum) => {
          return (
            <Tag
              color={protocolColor(protocol)}
              style={{
                marginInlineEnd: 0,
                fontFamily:
                  'JetBrains Mono, ui-monospace, SFMono-Regular, monospace',
                fontSize: 10,
                lineHeight: '16px'
              }}
            >
              {t(
                `workspace.authentication.customProviders.protocols.${protocol}`
              )}
            </Tag>
          )
        }
      },
      {
        title: t('workspace.authentication.customProviders.columns.enabled'),
        key: 'enabled',
        width: 90,
        align: 'center',
        render: (_v, record) => (
          <Switch
            size="small"
            checked={record.enabled}
            loading={
              toggleMutation.isPending &&
              toggleMutation.variables?.path?.provider_id === String(record.id)
            }
            onChange={(checked) =>
              toggleMutation.mutate({
                path: { id: workspaceId, provider_id: String(record.id) },
                body: { enabled: checked }
              })
            }
          />
        )
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 56,
        align: 'right',
        render: (_v, record) => (
          <Flex gap={4} justify="flex-end">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'edit',
                    icon: <Pencil size={14} />,
                    label: t(
                      'workspace.authentication.customProviders.actions.edit'
                    ),
                    onClick: () => setDrawer({ mode: 'edit', provider: record })
                  },
                  {
                    key: 'test',
                    icon: <Zap size={14} />,
                    label: soonLabel(
                      t('workspace.authentication.customProviders.actions.test')
                    ),
                    disabled: true
                  },
                  {
                    key: 'rotate',
                    icon: <RefreshCw size={14} />,
                    label: soonLabel(
                      t(
                        'workspace.authentication.customProviders.actions.rotate'
                      )
                    ),
                    disabled: true
                  },
                  {
                    key: 'audit',
                    icon: <HistoryIcon size={14} />,
                    label: soonLabel(
                      t(
                        'workspace.authentication.customProviders.actions.audit'
                      )
                    ),
                    disabled: true
                  },
                  { type: 'divider' },
                  {
                    key: 'delete',
                    icon: <Trash2 size={14} />,
                    label: (
                      <Popconfirm
                        title={t(
                          'workspace.authentication.customProviders.deleteConfirm',
                          { name: record.name }
                        )}
                        onConfirm={() =>
                          deleteMutation.mutate({
                            path: {
                              id: workspaceId,
                              provider_id: String(record.id)
                            }
                          })
                        }
                      >
                        {t(
                          'workspace.authentication.customProviders.actions.delete'
                        )}
                      </Popconfirm>
                    ),
                    danger: true
                  }
                ]
              }}
            >
              <Button
                size="small"
                type="text"
                icon={<MoreHorizontal size={14} />}
              />
            </Dropdown>
          </Flex>
        )
      }
    ],
    [t, token, toggleMutation, deleteMutation, workspaceId]
  )

  if (!idValid) {
    return null
  }

  return (
    <Flex vertical gap={14} className={styles.panelRoot}>
      {isError ? <QueryErrorAlert onRetry={() => void refetch()} /> : null}
      <ProTableContainer<OrganizationCustomAuthProvider>
        tableIdentifier="workspace-custom-auth-providers"
        searchPlaceholder={t(
          'workspace.authentication.customProviders.searchPlaceholder'
        )}
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={visibleProviders}
        showSorterTooltip={false}
        pagination={false}
        locale={{
          emptyText: (
            <Flex
              vertical
              align="center"
              justify="center"
              gap={10}
              style={{ padding: '48px 24px' }}
            >
              <span
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: 'var(--ant-color-fill-secondary)',
                  color: 'var(--ant-color-text-secondary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ShieldCheck size={20} />
              </span>
              <Text strong style={{ fontSize: 13 }}>
                {searchTerm
                  ? t('workspace.authentication.customProviders.noResultsTitle')
                  : t('workspace.authentication.customProviders.emptyTitle')}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: 12, textAlign: 'center', maxWidth: 360 }}
              >
                {searchTerm
                  ? t(
                      'workspace.authentication.customProviders.noResultsDescription',
                      { search: searchTerm }
                    )
                  : t(
                      'workspace.authentication.customProviders.emptyDescription'
                    )}
              </Text>
              {/* Offering "add a provider" under a no-match search would answer
                  the wrong question — the providers exist, the term just did
                  not match one. */}
              {searchTerm ? null : (
                <Button
                  type="primary"
                  icon={<Plus size={14} />}
                  onClick={() => setDrawer({ mode: 'create' })}
                >
                  {t('workspace.authentication.customProviders.add')}
                </Button>
              )}
            </Flex>
          )
        }}
        leftChildren={
          <Flex vertical style={{ lineHeight: 1.3, maxWidth: 720 }}>
            <Text strong style={{ fontSize: 14 }}>
              {t('workspace.authentication.customProviders.title')}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.authentication.customProviders.description')}
            </Text>
          </Flex>
        }
        rightChildren={
          <Flex gap={6}>
            <Button
              size="small"
              icon={<BookOpen size={14} />}
              href={DOCS_LINKS.customAuthProviders}
              target="_blank"
              rel="noreferrer"
            >
              {t('workspace.authentication.customProviders.docs')}
            </Button>
            <Button
              size="small"
              type="primary"
              icon={<Plus size={14} />}
              onClick={() => setDrawer({ mode: 'create' })}
            >
              {t('workspace.authentication.customProviders.add')}
            </Button>
          </Flex>
        }
      />

      <CustomProviderDrawer
        state={drawer}
        onClose={() => setDrawer({ mode: 'closed' })}
        onSubmit={(body) => {
          if (drawer.mode === 'edit') {
            updateMutation.mutate({
              path: {
                id: workspaceId,
                provider_id: String(drawer.provider.id)
              },
              body
            })
          } else {
            createMutation.mutate({
              path: { id: workspaceId },
              body
            })
          }
        }}
        onDelete={(providerId) => {
          deleteMutation.mutate(
            {
              path: { id: workspaceId, provider_id: String(providerId) }
            },
            {
              onSuccess: () => setDrawer({ mode: 'closed' })
            }
          )
        }}
        submitting={createMutation.isPending || updateMutation.isPending}
        deleting={deleteMutation.isPending}
      />
    </Flex>
  )
}
